import {Anchor, Breadcrumbs, Loader, Text, Tooltip} from '@mantine/core';
import {isArray} from 'lodash';
import {FC, memo, ReactNode} from 'react';
import {Link} from 'react-router-dom';
import {Highlight} from '../jobs/job-highlight';

export type BreadcrumbPart = {
  title: string;
  href?: string;
  copiable?: boolean;
  color?: string;
  isLoading?: boolean;
  description?: string;
};

type PageTitleProps = {
  crumbs: BreadcrumbPart | BreadcrumbPart[];
};

export const PageTitle: FC<PageTitleProps> = ({crumbs}) => {
  const parts = (isArray(crumbs) ? crumbs : [crumbs]) as BreadcrumbPart[];
  const partsRemoveNullTitles = parts.filter((part) => !!part.title);

  return (
    <>
      <Breadcrumbs separator='|' separatorMargin='sm' h={50}>
        {partsRemoveNullTitles.map((item, index) => (
          <Crumb index={index} {...item} key={`breadcrumb-${index}`} />
        ))}
      </Breadcrumbs>
    </>
  );
};

type CrumbProps = BreadcrumbPart & {
  index: number;
};

const Crumb = memo<CrumbProps>(
  ({title, href, index, copiable, color, description, isLoading}) => {
    const handleClick = () => {
      if (copiable) {
        navigator.clipboard.writeText(title);
      }
    };

    if (isLoading) {
      return <Loader size='xs' type='bars' />;
    }

    if (href) {
      return (
        <Anchor key={index} component={Link} to={href} fz='md'>
          {title}
        </Anchor>
      );
    }

    const content = (
      <CrumbTooltip description={description}>
        <Text
          fz='md'
          key={index}
          onClick={handleClick}
          style={crumbStyles.text(copiable)}
        >
          {title}
        </Text>
      </CrumbTooltip>
    );

    if (color) {
      return <Highlight color={color}>{content}</Highlight>;
    }

    return content;
  },
);

type CrumbTooltipProps = {
  children: ReactNode;
  description?: string;
};

const CrumbTooltip: FC<CrumbTooltipProps> = ({children, description}) => (
  <Tooltip
    p={3}
    fz='xs'
    color='dark'
    offset={-3}
    label={description}
    hidden={!description}
    styles={tooltipStyles}
  >
    {children}
  </Tooltip>
);

const crumbStyles = {
  text: (copiable?: boolean) => ({
    cursor: copiable ? 'pointer' : 'default',
  }),
  tooltip: {
    backgroundColor: 'transparent',
    color: '#333333',
    fontSize: '14px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    wordSpacing: '-1px',
  },
};

const tooltipStyles = {
  tooltip: crumbStyles.tooltip,
};
